import { useEffect, useRef, useState } from 'react';
import { Form } from 'antd';
import type { Dayjs } from 'dayjs';
import { getActiveContractKinds, uploadContracts } from 'api/contract.api';
import { presignPutExcel } from 'api/file.api';
import { ApiError } from 'types';
import type { ContractKind } from 'types/contract';
import type { ExcelData } from 'types/file';
import type {
  ContractImportFinding,
  ContractImportSummary,
} from 'types/contractImport';
import * as Sentry from '@sentry/react';

export interface ImportContext {
  fileName: string;
  contractKindId: string;
  contractKind: string;
  applicationDate: string;
  startDate: string;
  endDate?: string;
}
export interface RejectedImport {
  context: ImportContext;
  findings: ContractImportFinding[];
}
export interface ImportFormValues {
  contractKind: string;
  startDate: Dayjs;
  endDate?: Dayjs;
}
type ImportState =
  | { kind: 'editing' }
  | { kind: 'processing'; context: ImportContext }
  | { kind: 'rejected'; result: RejectedImport }
  | { kind: 'success'; context: ImportContext; summary?: ContractImportSummary }
  | {
      kind: 'upload-error' | 'uncertain' | 'access-error';
      context: ImportContext;
    };

const mimeTypes: Partial<Record<string, ExcelData['mime']>> = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  csv: 'text/csv',
};

export const useContractImport = () => {
  const [form] = Form.useForm<ImportFormValues>();
  const contractKindId = Form.useWatch('contractKind', form);
  const [state, setState] = useState<ImportState>({ kind: 'editing' });
  const [file, setFile] = useState<File>();
  const [selectionError, setSelectionError] = useState('');
  const [previous, setPrevious] = useState<RejectedImport>();
  const [showPrevious, setShowPrevious] = useState(false);
  const [contractKinds, setContractKinds] = useState<ContractKind[]>([]);
  const [kindsStatus, setKindsStatus] = useState<'loading' | 'ready' | 'error'>(
    'loading'
  );
  const [kindsAttempt, setKindsAttempt] = useState(0);
  const busy = useRef(false);
  const abort = useRef<AbortController | null>(null);
  const mounted = useRef(false);
  const picker = useRef<HTMLInputElement>(null);
  const selectedKind = contractKinds.find(
    (kind) => kind._id === contractKindId
  );
  const isTemporal = selectedKind?.term === 'temporal';

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      abort.current?.abort();
    };
  }, []);

  useEffect(() => {
    let active = true;
    getActiveContractKinds()
      .then(({ data }) => {
        if (active) {
          setContractKinds(data);
          setKindsStatus('ready');
        }
      })
      .catch(() => {
        if (active) setKindsStatus('error');
      });
    return () => {
      active = false;
    };
  }, [kindsAttempt]);

  const selectFile = (next: File) => {
    if (
      busy.current ||
      state.kind === 'uncertain' ||
      state.kind === 'access-error'
    )
      return;
    const extension =
      next.name.match(/\.(xlsx|xls|csv)$/i)?.[1].toLowerCase() ?? '';
    if (!mimeTypes[extension]) {
      setSelectionError(
        'Selecciona un archivo .xlsx, .xls o .csv. La selección anterior se conserva.'
      );
      return;
    }
    setSelectionError('');
    setFile(next);
    setShowPrevious(false);
    setState({ kind: 'editing' });
  };

  const submit = async (values: ImportFormValues) => {
    const kind = contractKinds.find((item) => item._id === values.contractKind);
    const mime = file
      ? mimeTypes[file.name.split('.').pop()!.toLowerCase()]
      : undefined;
    if (busy.current || state.kind !== 'editing' || !file || !kind || !mime)
      return;
    const temporal = kind.term === 'temporal';
    busy.current = true;
    const controller = new AbortController();
    abort.current = controller;
    const context: ImportContext = {
      fileName: file.name,
      contractKindId: kind._id,
      contractKind: kind.name,
      applicationDate: values.startDate.format('YYYY-MM-DD'),
      startDate: values.startDate.format('DD/MM/YYYY'),
      endDate: temporal ? values.endDate?.format('DD/MM/YYYY') : undefined,
    };
    setPrevious(undefined);
    setShowPrevious(false);
    setSelectionError('');
    setState({ kind: 'processing', context });
    let importStarted = false;
    try {
      const { data } = await presignPutExcel({ mime }, controller.signal);
      if (!data.url || !data.fileName)
        throw new Error('Invalid upload response');
      if (controller.signal.aborted) return;
      const uploaded = await fetch(data.url, {
        method: 'PUT',
        body: file,
        signal: controller.signal,
        headers: { 'Content-Type': mime },
      });
      if (!uploaded.ok) throw new Error('File upload failed');
      if (controller.signal.aborted) return;
      importStarted = true;
      const result = await uploadContracts({
        idFile: data.fileName,
        contractKind: values.contractKind,
        startDate: new Date(values.startDate.format('YYYY-MM-DD')),
        endDate:
          temporal && values.endDate
            ? new Date(values.endDate.format('YYYY-MM-DD'))
            : undefined,
        signal: controller.signal,
      });
      if (!mounted.current) return;
      if (result.kind === 'rejected') {
        const rejection = { context, findings: result.findings };
        setPrevious(rejection);
        setState({ kind: 'rejected', result: rejection });
      } else {
        setState({ kind: 'success', context, summary: result.summary });
        setFile(undefined);
      }
    } catch (error) {
      if (!mounted.current || controller.signal.aborted) return;
      if (error instanceof ApiError && [401, 403].includes(error.statusCode)) {
        setState({ kind: 'access-error', context });
        return;
      }
      if (!importStarted && !(error instanceof ApiError)) {
        // Never attach the presigned URL, file, options or raw transport error.
        Sentry.captureException(
          new Error('Contract import file upload failed before submission')
        );
      }
      setState({ kind: importStarted ? 'uncertain' : 'upload-error', context });
    } finally {
      busy.current = false;
    }
  };

  return {
    form,
    file,
    state,
    previous,
    showPrevious,
    setShowPrevious,
    picker,
    selectionError,
    selectFile,
    submit,
    contractKinds,
    kindsStatus,
    isTemporal,
    isBusy: state.kind === 'processing',
    retryKinds: () => {
      setKindsStatus('loading');
      setKindsAttempt((value) => value + 1);
    },
    openPicker: () => {
      if (!busy.current) picker.current?.click();
    },
    retryUpload: () => {
      if (!busy.current) setState({ kind: 'editing' });
    },
  };
};
